import { omitDeep } from 'deepdash/standalone';
import { removeEmptyDeep } from 'modules/workflows/components/NodeEditor/utils';
import { SingleValueSearchField, StartNodeData } from 'modules/workflows/types';

/**
 * Get comparable values from a given StartNodeEditor nodeData object.
 * - Only returns fields that are affected by UI changes
 *
 * @param { Object } data
 * @returns comparable nodeData object
 */
export const getStartNodeEditorNodeDataToBeCompared = (data: Partial<StartNodeData>) => {
  // GraphQL keys to be removed
  const keysToRemove = ['__typename'];
  const cleanedData = omitDeep(removeEmptyDeep(data), keysToRemove);
  // We only care about the selected values,
  // the rest either seems to change between renders without user interactions or are redundant
  if (Array.isArray(cleanedData?.searchCombo?.searchQueries)) {
    cleanedData.searchCombo.searchQueries = cleanedData.searchCombo.searchQueries.map(
      // FIXME: This is currently only checking values for single search fields.
      // Values for multi-value search fields and geo location search fields
      // have different values that need to be checked.
      ({ value, comparatorType, field }: SingleValueSearchField) => ({
        value,
        comparatorType,
        field,
      }),
    );
  }

  const selectedSkipDaysOfWeek = Object.entries(cleanedData?.skipDaysOfWeek || {}).filter(
    ([, value]) => value,
  );

  // If no days were selected by the user and are just initial values from the form
  // we set this to `null` because intial values would be equivalent to that
  if (cleanedData?.skipDaysOfWeek && selectedSkipDaysOfWeek.length === 0) {
    cleanedData.skipDaysOfWeek = null;
  }

  return {
    field: cleanedData?.field ?? undefined,
    nodeClass: cleanedData?.nodeClass ?? undefined,
    channelId: cleanedData?.channelId ?? undefined,
    searchCombo: cleanedData?.searchCombo ?? undefined,
    emailListId: cleanedData?.emailListId ?? undefined,
    messageTypeId: cleanedData?.messageTypeId ?? undefined,
    emailCampaignId: cleanedData?.emailCampaignId ?? undefined,
    workflowLifetimeLimit: cleanedData?.workflowLifetimeLimit ?? undefined,
    workflowSimultaneousLimit: cleanedData?.workflowSimultaneousLimit ?? undefined,
    recurDays: cleanedData?.recurDays ?? undefined,
    startDate: cleanedData?.startDate ?? undefined,
    skipDaysOfWeek: cleanedData?.skipDaysOfWeek ?? undefined,
    minuteOfDay: cleanedData?.minuteOfDay ?? undefined,
    maxStartTimeOffsetInMinutes: cleanedData?.maxStartTimeOffsetInMinutes ?? undefined,
    timeZone: cleanedData?.timeZone ?? undefined,
    endDate: cleanedData?.endDate ?? undefined,
    eventName: cleanedData?.eventName ?? undefined,
    suppressionListIds: cleanedData?.suppressionListIds ?? undefined,
    onNewUserCreation: cleanedData?.onNewUserCreation ?? undefined,
    workflowConcurrentJourneyEntranceCriteria:
      cleanedData?.workflowConcurrentJourneyEntranceCriteria ?? undefined,
  };
};
